import type { ChangeEvent } from "react";
import { forwardRef } from "react";
import type { Control } from "react-hook-form";
import { useController } from "react-hook-form";

import FormInput from "~/components/formElements/FormInput";
import type { FormInputBaseVariants } from "~/components/formElements/formInputBaseVariant.ts";
import { formInputBaseVariants } from "~/components/formElements/formInputBaseVariant.ts";
import FormInputError from "~/components/formElements/FormInputError";
import Label from "~/components/formElements/Label/Label";
import useAutoFocus from "~/hooks/useAutofocus.ts";

type SharedPropsType = & FormInputBaseVariants & {
	autoFocus?: boolean;
	label?: string;
	placeholder?: string;
	error?: string;
	rows?: number;
};

type TextareaPlainProps = SharedPropsType & {
	name?: string;
	value?: string;
	onChange?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
	onBlur?: () => void;
};

export const TextareaPlain = forwardRef<HTMLTextAreaElement, TextareaPlainProps>(
	({
		autoFocus=false,
		disabled,
		error,
		inputSize,
		label,
		name,
		onBlur,
		onChange,
		placeholder,
		rows = 3,
		theme,
		value = "",
	}, ref) => {
		const autoFocusRef = useAutoFocus(autoFocus, true, ref);

		return (
			<FormInput className="col-span-full">
				{label && <Label>{label}</Label>}
				<div>
					<textarea
						rows={rows}
						className={formInputBaseVariants({ disabled, inputSize, theme })}
						name={name}
						id={name}
						placeholder={placeholder}
						onKeyDown={(e) => {
							e.stopPropagation();
						}} // Form submission on enter is prevented in most parent form components
						value={value}
						ref={autoFocusRef}
						onChange={onChange}
						onBlur={onBlur}
					/>
				</div>
				{error && <FormInputError>{error}</FormInputError>}
			</FormInput>
		);
	},
);

TextareaPlain.displayName = "TextareaPlain";

type TextareaProps = SharedPropsType & {
	name: string;
	control: Control<any>;
};

const Textarea: React.FC<TextareaProps> = ({ name, control, label, placeholder, ...rest }) => {
	const {
		field: { ref, value, ...inputProps },
		fieldState: { error },
	} = useController({
		name,
		control,
	});

	return (
		<TextareaPlain
			ref={ref}
			value={value}
			{...inputProps}
			{...rest}
			label={label}
			placeholder={placeholder}
			error={error?.message}
		/>
	);
};

export default Textarea;